import { Box, Text, useThemeUI } from "theme-ui";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import castArray from "lodash/fp/castArray";
import filter from "lodash/fp/filter";
import flow from "lodash/fp/flow";
import get from "lodash/fp/get";
import getOr from "lodash/fp/getOr";
import head from "lodash/fp/head";
import mapValues from "lodash/fp/mapValues";

import LineTo from "react-lineto";

import { useTranslation } from "hooks";
import { selectUserChoiceValues, selectUIVersion } from "store/app";
import * as selectors from "store/app";
import { shouldBeDisplayed } from "utils";
import * as styles from "./PersonalizationCallOut.styles";
import PersonalizationCallOutV2 from "./PersonalizationCallOutV2";

function getBackgroundImageUrl({ url, userChoiceValues }) {
  if (typeof url === "string") {
    return url;
  }

  return flow(
    getOr([], "if"),
    filter(({ value: _value, ...conditions }) => {
      return Object.keys(conditions).every((fieldName) => {
        const displayIf = conditions[fieldName];
        const fieldValue = userChoiceValues[fieldName];

        if (!fieldValue) {
          return true;
        }

        if (displayIf.includes) {
          return displayIf.includes.includes(fieldValue);
        }

        if (displayIf.notIncludes) {
          return !displayIf.notIncludes.includes(fieldValue);
        }

        return true;
      });
    }),
    head,
    get("value")
  )(url);
}

export default function PersonalizationCallOut({
  backgroundColor,
  backgroundImage,
  position: initialPosition,
  zIndex,
  playerNameSelector,
  pathToValue,
  topText,
  title,
  footer,
}) {
  const { t } = useTranslation();
  const {
    theme: { isDesktop, isTablet, containerHeight },
  } = useThemeUI();

  const UIVersion = useSelector(selectUIVersion);

  const valueToDisplay = useSelector(get(pathToValue));

  const playerName = useSelector(
    playerNameSelector ? selectors[playerNameSelector] : () => ""
  );
  const noPersonalization = useSelector(
    getOr(false, ["app", "userChoiceValues", "noPersonalization"])
  );
  const userChoiceValues = useSelector(selectUserChoiceValues);

  const backgroundImageUrl = getBackgroundImageUrl({
    url: backgroundImage.url,
    userChoiceValues,
  });

  if (noPersonalization) {
    return null;
  }

  const { points: allPoints = [], ...position } = flow(
    castArray,
    filter(shouldBeDisplayed(userChoiceValues)),
    head,
    ({ css }) => {
      return isTablet && css.tablet
        ? css.tablet
        : getOr({}, [isDesktop ? "desktop" : "mobile"])(css);
    }
  )(initialPosition);

  const getPointClassName = (point, index) => {
    return `point-${index}-${point.top}-${point.left}-${Math.random()}`;
  };

  // ["point-0-{unique css}", "point-1-{unique css}", "point-2-{unique css}",...]
  const pointsNames = allPoints.map(getPointClassName);

  const lineFromToReducer = (acc, next) => {
    if (typeof acc === "string") {
      return [{ from: acc, to: next }];
    }
    return acc.concat([{ from: acc[acc.length - 1].to, to: next }]);
  };

  /*
  Multiple lines from different instances of PersonalizationCallOut requires unique css for each point
  Convert to:
  [
    { from: "point-0-{unique css}", to: "point-1-{unique css}"},
    { from: "point-1-{unique css}", to: "point-2-{unique css}"},
    ...
  ]
  */
  const lines =
    allPoints.length > 0 ? pointsNames.reduce(lineFromToReducer) : [];

  const [{ className: firstPointClassName = "", ...firstPoint }, ...points] =
    allPoints.length > 0
      ? allPoints
          .map(
            mapValues((val) => {
              const matches = val.match(/\d+vh/);
              if (!matches) {
                return val;
              }
              return matches.reduce((acc, match) => {
                const n = parseInt(match.match(/\d+/)[0], 10);
                const replacement = (n / 100) * containerHeight;
                return acc.replace(match, replacement + "px");
              }, val);
            })
          )
          .map((point, index) => {
            return {
              ...point,
              className: pointsNames[index],
            };
          })
      : [{}];

  const isEngineeredFor = pathToValue.includes("engineeredFor");

  if (UIVersion === 2) {
    return (
      <PersonalizationCallOutV2
        UIVersion={UIVersion}
        backgroundColor={backgroundColor}
        position={position}
        zIndex={zIndex}
        backgroundImageUrl={backgroundImageUrl}
        valueToDisplay={valueToDisplay}
        playerName={playerName}
        title={title}
        footer={footer}
      />
    );
  }

  return (
    <Box sx={styles.personalizationCallOut}>
      {firstPoint && (
        <Box
          className={firstPointClassName}
          sx={
            isTablet
              ? { ...firstPoint, transform: firstPoint.transformTablet }
              : { ...firstPoint }
          }
        />
      )}

      {lines.map(({ from, to }) => (
        <LineTo
          key={from + to}
          from={from}
          to={to}
          borderColor="black"
          borderWidth={2}
          delay={0}
          className={"lineToStyle"}
          zIndex={1000}
        />
      ))}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Box
          sx={styles.callout({
            backgroundColor,
            position,
            zIndex,
          })}
        >
          <Box
            sx={styles.calloutImage({
              backgroundImageUrl,
            })}
          >
            {points.map(({ className, ...point }, index) => (
              <Box key={index} className={className} sx={point} />
            ))}

            <Text as="div" sx={styles.disclaimer}>
              {topText}
            </Text>

            <Text
              as="div"
              className="text"
              sx={styles.text({
                isEngineeredFor,
              })}
            >
              {isEngineeredFor ? t("Engineered For: ") : ""}
              {valueToDisplay || playerName}
            </Text>
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
}
